<template>
  <v-app class="scan-qr-layout">
    <v-app-bar
      app
      flat
      height="65"
      class="qr-bar"
      :class="{ 'qr-bar--scrolled': scrolled }"
    >
      <div class="container-xl d-flex align-center px-0" style="gap: 10px">
        <ScanQrBarLogo />
        <v-toolbar-title class="flex-grow-1 min-width-0">{{
          $t("qr_order_items")
        }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <QrLanguageMenu />
      </div>
    </v-app-bar>

    <v-main v-if="fetched" class="scan-receipt-page">
      <div class="container-xl qr-receipt-content">
        <!-- Buffet plan card (mirrors cart.vue qr-cart-plan-card) -->
        <div v-if="buffetPlans.length" class="qr-cart-plan-card mb-4">
          <div class="qr-cart-plan-card__header">
            <div class="qr-cart-plan-card__icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 3v8a3 3 0 003 3v7M7 3v6M10 3v6M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="qr-cart-plan-card__info">
              <div class="qr-cart-plan-card__name">
                {{ $t("buffet_plans") }}
              </div>
              <div class="qr-cart-plan-card__sub">
                {{ $t("buffet_persons") }} · {{ $t("included") }}
              </div>
            </div>
            <div class="qr-cart-plan-card__badge">
              {{ $t("confirmed") || "Confirmed" }}
            </div>
          </div>

          <div
            v-for="(plan, idx) in buffetPlans"
            :key="'bp-' + idx"
            class="qr-cart-plan-card__body"
            :class="{
              'qr-cart-plan-card__body--bordered': idx < buffetPlans.length - 1,
            }"
          >
            <div>
              <div class="qr-cart-plan-card__plan-name">
                {{ plan.plan_name }}
              </div>
              <div class="qr-cart-plan-card__plan-detail">
                {{ plan.persons }} × {{ receiptCurrencySymbol
                }}{{ Number(plan.unit_price || 0) | NumberFormat }}
              </div>
            </div>
            <div class="qr-cart-plan-card__total">
              {{ receiptCurrencySymbol
              }}{{ Number(plan.subtotal || 0) | NumberFormat }}
            </div>
          </div>
        </div>

        <!-- Products section -->
        <template v-if="products.length">
          <div class="qr-cart-section">
            <div v-if="buffetPlans.length" class="qr-cart-section__header">
              <div class="qr-cart-section__title">{{ $t("paid_extra") }}</div>
              <div class="qr-cart-section__sub">
                {{ products.length }} {{ $t("added") || "added" }}
              </div>
            </div>
            <div class="qr-cart-lines">
              <div
                v-for="(item, index) in products"
                :key="index"
                class="qr-cart-line-card"
              >
                <div class="qr-cart-line-card__thumb">
                  <ScanProductImage
                    :img="item.product_id.img"
                    :contain="true"
                    width="56"
                    height="56"
                  />
                </div>
                <div class="qr-cart-line-card__info">
                  <div class="qr-cart-line-card__name">
                    {{ getProductName(item.product_id) }}
                  </div>
                  <div class="qr-cart-line-card__sub">
                    {{ item.qty | NumberFormat }} ×
                    {{ getCurrencySymbol(item.product_id)
                    }}{{ (item.price_sale || item.product_id.price_sale) | NumberFormat }}
                  </div>
                </div>
                <div class="qr-cart-line-card__total qr-accent-text">
                  {{ getCurrencySymbol(item.product_id)
                  }}{{ (item.total_price || (item.product_id.price_sale * item.qty)) | NumberFormat }}
                </div>
              </div>
            </div>
          </div>

          <!-- Totals breakdown -->
          <div class="qr-cart-totals-card mb-3">
            <div v-if="buffetPlans.length" class="qr-totals-row">
              <span>{{ $t("buffet_plans") }}</span>
              <span
                >{{ receiptCurrencySymbol
                }}{{ buffetPlansTotal | NumberFormat }}</span
              >
            </div>
            <div
              v-if="buffetPlans.length && products.length"
              class="qr-totals-row"
            >
              <span>{{ $t("paid_extra") }}</span>
              <span
                >{{ receiptCurrencySymbol
                }}{{ productsTotal | NumberFormat }}</span
              >
            </div>
            <div v-if="!buffetPlans.length" class="qr-totals-row">
              <span>{{ $t("amount_to_pay") }}</span>
              <span
                >{{ receiptCurrencySymbol
                }}{{ productsTotal | NumberFormat }}</span
              >
            </div>
            <!-- Grand total replaces service charge -->
            <div class="qr-receipt-grand-total">
              <span class="qr-receipt-grand-total__label">{{
                $t("grand_total")
              }}</span>
              <span class="qr-receipt-grand-total__value">
                {{ receiptCurrencySymbol
                }}{{ displayGrandTotal | NumberFormat }}
              </span>
            </div>
          </div>
        </template>

        <!-- Buffet-only empty state -->
        <template v-else-if="buffetPlans.length">
          <!-- Totals card with grand total for buffet-only -->
          <div class="qr-cart-totals-card mb-4">
            <div class="qr-receipt-grand-total">
              <span class="qr-receipt-grand-total__label">{{
                $t("grand_total")
              }}</span>
              <span class="qr-receipt-grand-total__value">
                {{ receiptCurrencySymbol
                }}{{ displayGrandTotal | NumberFormat }}
              </span>
            </div>
          </div>
          <div class="qr-receipt-buffet-empty">
            <v-icon size="44" color="primary" class="mb-3"
              >fal fa-utensils</v-icon
            >
            <h3 class="qr-receipt-buffet-empty__title">
              {{ $t("buffet_only_no_items") }}
            </h3>
            <p class="qr-receipt-buffet-empty__desc">
              {{ $t("buffet_only_no_items_desc") }}
            </p>
            <button class="qr-btn-cta mt-2" @click="goBrowseMenu">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                style="flex-shrink: 0"
              >
                <path
                  d="M7 3v8a3 3 0 003 3v7M7 3v6M10 3v6M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              {{ $t("browse_menu") }}
            </button>
          </div>
        </template>

        <!-- Fully empty state -->
        <div v-else class="qr-receipt-empty">
          <v-icon size="72" color="grey lighten-2" class="mb-4"
            >fal fa-receipt</v-icon
          >
          <h3 class="qr-receipt-empty__title">{{ $t("have_no_data") }}</h3>
          <p class="qr-receipt-empty__desc">
            {{ $t("qr_order_items_empty_description") }}
          </p>
          <button class="qr-btn-cta mt-2" @click="$router.go(-1)">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              style="flex-shrink: 0"
            >
              <path
                d="M19 12H5M5 12l7 7M5 12l7-7"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ $t("back") }}
          </button>
        </div>
      </div>
    </v-main>

    <ScanBottomNav />
    <app-loading :overlay="overlay" />
  </v-app>
</template>

<script>
import { myMixin } from "@/mixin/main";
import { mapGetters, mapMutations } from "vuex";
import ScanProductImage from "@/components/scan/ScanProductImage.vue";
import ScanBottomNav from "@/components/scan/ScanBottomNav.vue";
import ScanQrBarLogo from "@/components/scan/ScanQrBarLogo.vue";
import QrLanguageMenu from "@/components/qr_order/QrLanguageMenu.vue";

export default {
  mixins: [myMixin],
  components: {
    ScanProductImage,
    ScanBottomNav,
    ScanQrBarLogo,
    QrLanguageMenu,
  },
  data: () => ({
    products: [],
    ticket: null,
    fetched: false,
    scrolled: false,
  }),
  mounted() {
    this._onScroll = () => {
      this.scrolled = window.scrollY > 8;
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });
    this.fetch_product();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this._onScroll);
  },
  computed: {
    ...mapGetters({
      overlay: "index/overlay",
      locales: "auth/locales",
    }),
    buffetPlans() {
      const t = this.ticket;
      if (!t) return [];
      return t.buffetPlans || t.buffet_plans || [];
    },
    buffetPlansTotal() {
      return this.buffetPlans.reduce((s, p) => s + Number(p.subtotal || 0), 0);
    },
    productsTotal() {
      return this.products.reduce((a, b) => {
        const price = Number(b.total_price || 0) || (Number(b.product_id?.price_sale || 0) * Number(b.qty || 0));
        return a + price;
      }, 0);
    },
    displayGrandTotal() {
      if (this.ticket != null && this.ticket.total_price != null) {
        return Number(this.ticket.total_price);
      }
      return this.productsTotal;
    },
    receiptCurrencySymbol() {
      const rows = Array.isArray(this.products) ? this.products : [];
      if (!rows.length) return "₭";
      const firstSymbol = rows[0]?.product_id?.currency?.symbol || "";
      if (!firstSymbol) return "₭";
      const isMixed = rows.some((r) => {
        const symbol = r?.product_id?.currency?.symbol || "";
        return symbol && symbol !== firstSymbol;
      });
      return isMixed ? "" : firstSymbol;
    },
  },
  methods: {
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),
    getCurrencySymbol(item) {
      return item && item.currency && item.currency.symbol
        ? item.currency.symbol
        : "₭";
    },
    async fetch_product() {
      this.fetched = false;
      try {
        this.SET_OVERLAY(true);
        const { data } = await this.$http.get("/qr/order");
        if (data?.data !== null && data?.data !== undefined) {
          this.ticket = data.data;
          const object = await Help.reduceProduct(data?.data);
          this.products = object ?? [];
        } else {
          this.ticket = null;
          this.products = [];
        }
      } catch (error) {
        console.log(error);
        this.ticket = null;
        this.products = [];
      } finally {
        this.SET_OVERLAY(false);
        this.fetched = true;
      }
    },
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
    },
    goBrowseMenu() {
      const s = localStorage.getItem("store_id");
      const b = localStorage.getItem("branch_id");
      const t = localStorage.getItem("table_id");
      if (s && b && t) {
        this.$router.push(`/scan/${s}/${b}/${t}`);
      } else {
        this.$router.go(-1);
      }
    },
  },
};
</script>

<style scoped>
.scan-receipt-page {
  background-color: var(--qr-bg);
  touch-action: manipulation;
  padding-bottom: 0;
}

/* App bar — matches cart.vue exactly */
.qr-bar {
  background: var(--qr-appbar-tint-clear) !important;
  transition: background 0.2s, border-color 0.2s;
  border-bottom: 0.5px solid transparent !important;
}

.qr-bar--scrolled {
  background: var(--qr-appbar-tint-scrolled) !important;
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  border-bottom-color: rgba(40, 28, 16, 0.07) !important;
}

.qr-bar ::v-deep .v-toolbar__content {
  background: transparent !important;
}

/* Content wrapper */
.qr-receipt-content {
  padding-top: 16px;
  padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
}

/* Buffet plan card — identical to cart.vue */
.qr-cart-plan-card {
  background: var(--qr-card-bg);
  border: 0.5px solid var(--qr-card-border);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(40, 28, 16, 0.06);
}

.qr-cart-plan-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 10px;
  border-bottom: 0.5px solid var(--qr-card-border);
}

.qr-cart-plan-card__icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--qr-accent-soft);
  color: var(--qr-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qr-cart-plan-card__info {
  flex: 1;
  min-width: 0;
}

.qr-cart-plan-card__name {
  font-size: 14px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.2px;
}

.qr-cart-plan-card__sub {
  font-size: 11px;
  color: var(--qr-muted);
  margin-top: 2px;
}

.qr-cart-plan-card__badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--qr-included-bg);
  color: var(--qr-included-fg);
  flex-shrink: 0;
}

.qr-cart-plan-card__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  gap: 10px;
}

.qr-cart-plan-card__body--bordered {
  border-bottom: 0.5px solid var(--qr-card-border);
}

.qr-cart-plan-card__plan-name {
  font-size: 14.5px;
  font-weight: 600;
  color: var(--qr-text);
  letter-spacing: -0.1px;
}

.qr-cart-plan-card__plan-detail {
  font-size: 11.5px;
  color: var(--qr-muted);
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.qr-cart-plan-card__total {
  font-size: 16px;
  font-weight: 700;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.3px;
  flex-shrink: 0;
}

/* Section headers */
.qr-cart-section {
  margin-bottom: 20px;
}

.qr-cart-section__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 0 4px;
}

.qr-cart-section__title {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.3px;
}

.qr-cart-section__sub {
  font-size: 11px;
  color: var(--qr-muted);
  margin-left: 8px;
}

/* Cart line cards — identical to cart.vue */
.qr-cart-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-cart-line-card {
  background: var(--qr-card-bg);
  border: 0.5px solid var(--qr-card-border);
  border-radius: 14px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 3px 10px rgba(40, 28, 16, 0.04);
}

.qr-cart-line-card__thumb {
  width: 56px;
  height: 56px;
  border-radius: 11px;
  overflow: hidden;
  flex-shrink: 0;
}

.qr-cart-line-card__info {
  flex: 1;
  min-width: 0;
}

.qr-cart-line-card__name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--qr-text);
  letter-spacing: -0.1px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qr-cart-line-card__sub {
  font-size: 11px;
  color: var(--qr-muted);
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
}

.qr-cart-line-card__total {
  min-width: 58px;
  text-align: right;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
  flex-shrink: 0;
}

.qr-accent-text {
  color: var(--qr-accent) !important;
}

/* Totals breakdown */
.qr-cart-totals-card {
  background: var(--qr-subtle-bg);
  border: 0.5px solid var(--qr-card-border);
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-totals-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 12.5px;
  color: var(--qr-muted);
}

.qr-totals-row span:last-child {
  font-size: 13px;
  font-weight: 600;
  color: var(--qr-text);
  font-variant-numeric: tabular-nums;
}

/* Empty states */
.qr-receipt-buffet-empty,
.qr-receipt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
}

.qr-receipt-buffet-empty__title,
.qr-receipt-empty__title {
  font-size: 18px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.3px;
  margin-bottom: 6px;
}

.qr-receipt-buffet-empty__desc,
.qr-receipt-empty__desc {
  font-size: 13px;
  color: var(--qr-muted);
  line-height: 1.45;
  margin-bottom: 0;
}

/* CTA button */
.qr-btn-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  padding: 0 24px;
  border-radius: 999px;
  border: 0;
  background: var(--qr-cta-gradient);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: var(--qr-cta-shadow);
  touch-action: manipulation;
  transition: box-shadow 0.15s, transform 0.1s;
}

.qr-btn-cta:active {
  transform: scale(0.97);
  box-shadow: var(--qr-cta-shadow-active);
}

/* Grand total row — sits inside qr-cart-totals-card replacing service charge */
.qr-receipt-grand-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.qr-receipt-grand-total__label {
  font-size: 12.5px;
  color: var(--qr-muted);
  font-weight: 600;
}

.qr-receipt-grand-total__value {
  font-size: 16px;
  font-weight: 800;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
}

@media (prefers-reduced-motion: reduce) {
  .qr-btn-cta,
  .qr-cart-line-card {
    transition: none !important;
  }
}
</style>
